import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Music, Search, SlidersHorizontal, X, Grid3X3, LayoutList, Tag } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArtistGridCard, ArtistListItem } from '../components/browse';
import { BACKEND_URL } from '../lib/backend';

const GENRE_CONFIG = {
  'dancehall': { label: 'Dancehall' },
  'reggae': { label: 'Reggae' },
  'soca': { label: 'Soca' }
};

export default function BrowseArtists() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableGenres, setAvailableGenres] = useState([]);
  const { isAuthenticated, user } = useAuth();

  const isArtist = user?.role === 'artist';
  const canSeePrices = isAuthenticated && !isArtist;

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [priceRange, setPriceRange] = useState('all');
  const [genreFilter, setGenreFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // Intentionally [] — fetch on mount only; api/toast/process.env are stable
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [artistsRes, genresRes] = await Promise.all([
          api.getArtists(),
          fetch(`${BACKEND_URL}/api/genres`).then(r => r.json())
        ]);
        setArtists(artistsRes.data);
        setAvailableGenres(genresRes.genres || []);
      } catch (error) {
        toast.error('Failed to load artists');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredArtists = useMemo(() => {
    let result = [...artists];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(a => a.name.toLowerCase().includes(query) || a.bio.toLowerCase().includes(query));
    }
    if (genreFilter !== 'all') result = result.filter(a => a.genres?.includes(genreFilter));
    if (priceRange !== 'all') {
      const filters = { under300: a => a.price < 300, '300to400': a => a.price >= 300 && a.price <= 400, over400: a => a.price > 400 };
      if (filters[priceRange]) result = result.filter(filters[priceRange]);
    }
    const verifiedFirst = (a, b) => (b.is_verified ? 1 : 0) - (a.is_verified ? 1 : 0);
    const sorters = {
      name: (a, b) => verifiedFirst(a, b) || a.name.localeCompare(b.name),
      price_low: (a, b) => verifiedFirst(a, b) || a.price - b.price,
      price_high: (a, b) => verifiedFirst(a, b) || b.price - a.price,
      rating: (a, b) => verifiedFirst(a, b) || (b.average_rating || 0) - (a.average_rating || 0),
      delivery: (a, b) => verifiedFirst(a, b) || a.delivery_days - b.delivery_days,
    };
    if (sorters[sortBy]) result.sort(sorters[sortBy]);
    return result;
  }, [artists, searchQuery, sortBy, priceRange, genreFilter]);

  const clearFilters = () => { setSearchQuery(''); setSortBy('name'); setPriceRange('all'); setGenreFilter('all'); };
  const hasActiveFilters = searchQuery || sortBy !== 'name' || priceRange !== 'all' || genreFilter !== 'all';

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

  const renderGenreSelect = (className) => (
    <Select value={genreFilter} onValueChange={setGenreFilter}>
      <SelectTrigger className={`${className} input-dark`} data-testid="genre-filter"><Tag className="w-4 h-4 mr-2" /><SelectValue placeholder="Genre" /></SelectTrigger>
      <SelectContent className="bg-surface border-[#262626]">
        <SelectItem value="all">All Genres</SelectItem>
        {availableGenres.map(g => <SelectItem key={g} value={g}>{GENRE_CONFIG[g]?.label || g}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const renderSortSelect = (className) => (
    <Select value={sortBy} onValueChange={setSortBy}>
      <SelectTrigger className={`${className} input-dark`} data-testid="sort-select"><SelectValue placeholder="Sort by" /></SelectTrigger>
      <SelectContent className="bg-surface border-[#262626]">
        <SelectItem value="name">Name (A-Z)</SelectItem>
        {canSeePrices && <SelectItem value="price_low">Price: Low to High</SelectItem>}
        {canSeePrices && <SelectItem value="price_high">Price: High to Low</SelectItem>}
        {canSeePrices && <SelectItem value="rating">Highest Rated</SelectItem>}
        <SelectItem value="delivery">Fastest Delivery</SelectItem>
      </SelectContent>
    </Select>
  );

  const renderPriceSelect = (className) => canSeePrices && (
    <Select value={priceRange} onValueChange={setPriceRange}>
      <SelectTrigger className={`${className} input-dark`} data-testid="price-filter"><SelectValue placeholder="Price Range" /></SelectTrigger>
      <SelectContent className="bg-surface border-[#262626]">
        <SelectItem value="all">All Prices</SelectItem>
        <SelectItem value="under300">Under $300</SelectItem>
        <SelectItem value="300to400">$300 - $400</SelectItem>
        <SelectItem value="over400">Over $400</SelectItem>
      </SelectContent>
    </Select>
  );

  const renderViewToggle = (extraClass = '') => (
    <div className={`flex items-center gap-1 bg-surface rounded-lg p-1 border border-[#262626] ${extraClass}`}>
      <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-lime text-black' : 'text-[#A1A1AA] hover:text-white'}`} data-testid="grid-view-btn"><Grid3X3 className="w-4 h-4" /></button>
      <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-lime text-black' : 'text-[#A1A1AA] hover:text-white'}`} data-testid="list-view-btn"><LayoutList className="w-4 h-4" /></button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">Browse Artists</h1>
            <p className="text-[#A1A1AA]">Discover and book custom dubplates from top Dancehall and Reggae artists</p>
          </motion.div>

          {/* Filters */}
          <div className="mb-8 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
                <Input type="text" placeholder="Search artists by name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="input-dark pl-10 w-full" data-testid="artist-search" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"><X className="w-4 h-4" /></button>}
              </div>
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="sm:hidden border-[#262626]"><SlidersHorizontal className="w-4 h-4 mr-2" />Filters</Button>
              <div className="hidden sm:flex gap-4 flex-wrap">
                {renderGenreSelect('w-[160px]')}
                {renderSortSelect('w-[160px]')}
                {renderPriceSelect('w-[160px]')}
                {hasActiveFilters && <Button variant="ghost" onClick={clearFilters} className="text-[#A1A1AA] hover:text-white" data-testid="clear-filters"><X className="w-4 h-4 mr-1" />Clear</Button>}
                {renderViewToggle()}
              </div>
            </div>

            {showFilters && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="sm:hidden flex flex-col gap-4 p-4 bg-surface rounded-xl border border-[#262626]">
                {renderGenreSelect('w-full')}
                {renderSortSelect('w-full')}
                {renderPriceSelect('w-full')}
                <div className="flex items-center justify-center gap-2"><span className="text-sm text-[#A1A1AA]">View:</span>{renderViewToggle()}</div>
                {hasActiveFilters && <Button variant="outline" onClick={clearFilters} className="border-[#262626]"><X className="w-4 h-4 mr-1" />Clear Filters</Button>}
              </motion.div>
            )}

            {!loading && <p className="text-sm text-[#A1A1AA]">Showing {filteredArtists.length} of {artists.length} artists{searchQuery && ` for "${searchQuery}"`}</p>}
          </div>

          {/* Content */}
          {loading ? (
            <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className={`animate-pulse ${viewMode === 'grid' ? 'artist-card' : 'bg-surface rounded-xl p-4 flex gap-4'}`}>
                  {viewMode === 'grid' ? (<><div className="aspect-[4/3] bg-surface-highlight" /><div className="p-6"><div className="h-6 bg-surface-highlight rounded mb-3 w-3/4" /><div className="h-4 bg-surface-highlight rounded mb-4 w-full" /><div className="h-8 bg-surface-highlight rounded w-1/3" /></div></>) : (<><div className="w-32 h-32 bg-surface-highlight rounded-lg flex-shrink-0" /><div className="flex-1"><div className="h-6 bg-surface-highlight rounded mb-2 w-1/3" /><div className="h-4 bg-surface-highlight rounded mb-2 w-full" /><div className="h-4 bg-surface-highlight rounded w-2/3" /></div></>)}
                </div>
              ))}
            </div>
          ) : filteredArtists.length === 0 ? (
            <div className="text-center py-20">
              <Music className="w-16 h-16 mx-auto mb-4 text-[#333]" />
              <h3 className="text-xl font-heading font-bold mb-2">{artists.length === 0 ? 'No Artists Yet' : 'No Artists Found'}</h3>
              <p className="text-[#A1A1AA] mb-4">{artists.length === 0 ? 'Artists will be added soon. Check back later!' : 'Try adjusting your search or filters'}</p>
              {hasActiveFilters && <Button onClick={clearFilters} className="btn-secondary">Clear Filters</Button>}
            </div>
          ) : (
            <motion.div key={`${viewMode}-${genreFilter}-${priceRange}-${sortBy}`} variants={containerVariants} initial="hidden" animate="visible" className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}>
              {filteredArtists.map((artist, index) => (
                <motion.div key={artist.id} variants={itemVariants}>
                  {viewMode === 'grid' ? (
                    <ArtistGridCard artist={artist} index={index} canSeePrices={canSeePrices} isArtist={isArtist} />
                  ) : (
                    <ArtistListItem artist={artist} index={index} canSeePrices={canSeePrices} isArtist={isArtist} />
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
